"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";
import Link from "next/link";

const milestones = [
{
  year: "2020",
  title: "上海公司成立",
  desc: "信悅日用品在上海正式成立，開始建立日用品貿易業務網絡"
},
{
  year: "2022",
  title: "供應鏈擴展",
  desc: "與超過 50 家優質工廠建立長期合作關係，產品線擴展至 300+ 種"
},
{
  year: "2024",
  title: "進軍香港市場",
  desc: "在香港新界葵涌設立辦事處，正式拓展香港及大灣區業務"
},
{
  year: "2026",
  title: "持續成長",
  desc: "產品種類突破 500+，服務客戶超過 100 家，業務持續穩健增長"
}];


const values = [
{
  icon: "HandThumbUpIcon",
  title: "誠信經營",
  desc: "以誠信為本，為每位客戶提供透明、公正的商業服務"
},
{
  icon: "SparklesIcon",
  title: "品質至上",
  desc: "嚴格把控產品品質，確保每件商品符合最高標準"
},
{
  icon: "BoltIcon",
  title: "高效服務",
  desc: "快速響應客戶需求，確保訂單準時交付"
},
{
  icon: "GlobeAltIcon",
  title: "可持續發展",
  desc: "推廣環保產品，致力於可持續的貿易發展模式"
}];


export default function AboutContent() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("revealed");
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    sectionRef.current?.querySelectorAll(".reveal-hidden").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={sectionRef} className="container-main pb-24">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-white/30 mb-10">
        <Link href="/homepage" className="hover:text-white/60 transition-colors">首頁</Link>
        <span>/</span>
        <span className="text-gold-DEFAULT">關於我們</span>
      </div>

      {/* Hero split */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24 reveal-hidden">
        <div>
          <span className="text-xs text-gold-DEFAULT uppercase tracking-widest font-semibold">關於信悅</span>
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-white mt-3 mb-6 leading-tight">
            連接上海與香港<br />
            <span className="text-gradient-gold">的日用品橋樑</span>
          </h1>
          <p className="text-white/60 leading-relaxed mb-5">
            信悅日用品有限公司（XinyueDailyNecessities Co., Ltd.）成立於上海，是一家專注於日用品貿易的專業公司。我們以上海為總部，在香港設立辦事處，致力於為大灣區及香港客戶提供優質、多元的日用品採購服務。
          </p>
          <p className="text-white/50 leading-relaxed mb-8">
            我們的業務涵蓋清潔用品、個人護理、廚房用品、辦公用品及嬰幼兒用品等多個品類，擁有超過 500 種產品，服務逾 100 家企業客戶。
          </p>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6">
            {[
            { num: "500+", label: "產品種類" },
            { num: "100+", label: "合作客戶" },
            { num: "5年+", label: "行業經驗" }].
            map((s) =>
            <div key={s.label} className="glass-card rounded-xl p-4 text-center">
                <p className="stat-number text-2xl md:text-3xl">{s.num}</p>
                <p className="text-xs text-white/40 mt-1">{s.label}</p>
              </div>
            )}
          </div>
        </div>

        <div className="relative">
          <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
            <AppImage
              src="https://img.rocket.new/generatedImages/rocket_gen_img_13df49c9b-1771933250668.png"
              alt="信悅日用品公司辦公大樓外觀，現代商業建築"
              fill
              className="object-cover" />
            
            <div className="absolute inset-0 bg-gradient-to-t from-brand-bg/80 to-transparent" />
          </div>

          {/* Floating info card */}
          <div className="absolute -bottom-6 -left-6 glass-card-gold rounded-xl p-5 max-w-[200px]">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full bg-green-400 blink" />
              <span className="text-xs text-white/60">業務運營中</span>
            </div>
            <p className="font-serif text-white text-sm font-semibold">香港 · 上海</p>
            <p className="text-xs text-gold-DEFAULT mt-1">雙城市業務佈局</p>
          </div>
        </div>
      </div>

      {/* Company values */}
      <div className="mb-24">
        <div className="text-center mb-12 reveal-hidden">
          <span className="text-xs text-gold-DEFAULT uppercase tracking-widest font-semibold">企業理念</span>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mt-2">
            我們的核心價值
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {values.map((v, i) =>
          <div key={v.title} className={`glass-card rounded-card p-6 hover:border-gold-DEFAULT/30 transition-all duration-300 reveal-hidden delay-${i + 1}`}>
              <div className="w-11 h-11 rounded-xl bg-gold-DEFAULT/10 border border-gold-DEFAULT/20 flex items-center justify-center mb-4">
                <Icon name={v.icon as any} size={20} className="text-gold-DEFAULT" />
              </div>
              <h3 className="font-serif text-base font-semibold text-white mb-2">{v.title}</h3>
              <p className="text-sm text-white/50 leading-relaxed">{v.desc}</p>
            </div>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="mb-24">
        <div className="text-center mb-12 reveal-hidden">
          <span className="text-xs text-gold-DEFAULT uppercase tracking-widest font-semibold">發展歷程</span>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mt-2">
            信悅的成長足跡
          </h2>
        </div>
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-gold-DEFAULT/30 to-transparent" />

          <div className="space-y-10">
            {milestones.map((m, i) =>
            <div
              key={m.year}
              className={`relative flex flex-col md:flex-row gap-8 ${
              i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"} reveal-hidden delay-${
              i + 1}`}>
              
                {/* Content */}
                <div className={`flex-1 ${i % 2 === 0 ? "md:text-right md:pr-12" : "md:pl-12"} pl-20 md:pl-0`}>
                  <div className="glass-card rounded-xl p-5 inline-block text-left">
                    <p className="text-xs text-gold-DEFAULT font-bold tracking-widest mb-1">{m.year}</p>
                    <h3 className="font-serif text-base font-semibold text-white mb-1">{m.title}</h3>
                    <p className="text-sm text-white/50">{m.desc}</p>
                  </div>
                </div>

                {/* Dot */}
                <div className="absolute left-6 md:left-1/2 md:-translate-x-1/2 top-5 w-4 h-4 rounded-full bg-gold-DEFAULT border-2 border-brand-bg" />

                {/* Spacer */}
                <div className="flex-1 hidden md:block" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Office cards */}
      <div className="reveal-hidden">
        <div className="text-center mb-10">
          <span className="text-xs text-gold-DEFAULT uppercase tracking-widest font-semibold">辦事處</span>
          <h2 className="font-serif text-3xl font-bold text-white mt-2">雙城市辦公室</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
          {
            city: "香港辦事處",
            flag: "🇭🇰",
            address: "香港 新界葵涌\n貴盛工業大廈一期",
            contact: "WhatsApp: 852 9492 7816",
            img: "https://images.unsplash.com/photo-1675949830052-2f6a576b1488",
            alt: "香港維多利亞港夜景，城市燈光璀璨"
          },
          {
            city: "上海總公司",
            flag: "🇨🇳",
            address: "中國 上海市\nShanghai Headquarters",
            contact: "總公司業務中心",
            img: "https://images.unsplash.com/photo-1603616338762-43689389d27b",
            alt: "上海陸家嘴金融區夜景，高樓大廈林立"
          }].
          map((office) =>
          <div key={office.city} className="glass-card rounded-2xl overflow-hidden group hover:border-gold-DEFAULT/30 transition-all duration-300">
              <div className="relative h-48 overflow-hidden">
                <AppImage
                src={office.img}
                alt={office.alt}
                fill
                className="object-cover product-img" />
              
                <div className="img-overlay absolute inset-0" />
                <div className="absolute top-4 left-4">
                  <span className="text-2xl">{office.flag}</span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-serif text-lg font-semibold text-white mb-2">{office.city}</h3>
                <p className="text-white/50 text-sm whitespace-pre-line mb-2">{office.address}</p>
                <p className="text-gold-DEFAULT text-sm">{office.contact}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>);

}